import { useState, useEffect, useCallback, useContext } from 'react';
import { PaymentPreferencesModel } from '../../../data-models';
import { Link } from 'react-router-dom';
import { get, prefixObjectValues, getHtmlFromMarkdown } from '../../../utils';
import styled from 'styled-components';
import { RefreshContext } from '../../../contexts/refresh-context';
import { Form, FormElement } from '@progress/kendo-react-form';
import { RadioButton } from '@progress/kendo-react-inputs';
import { ClipLoader } from 'react-spinners';
import { ToastTypes, AppPaths, ButtonTypes } from '../../../utils/enums';
import {
  getPaymentPreferences,
  postPaymentPreferences,
} from '../../../utils/web-apis-client';
import {
  ReadOnlyContainer,
  OpenSansSemiBoldLabelMod,
  SubmitButton,
  EditButtonMod,
  CancelButton,
  SectionTitleMod,
  EditImage,
  StyledSpinner,
  toastMessages,
  ErrorText,
  SectionSubTitleMod,
  Container,
  PrefSeparator,
} from '../index.sc';
import {
  SectionSeparator,
  StyledNotification,
  ToastNotificationGroupStyle,
} from '../../base/simple-styled-components/index';
import {
  EftPreferenceOptions,
  templateIds as EftIds,
} from './eft-preference-options';
import { CheckPreferenceOptions } from './check-preference-options';
import {
  ZellePreferenceOptions,
  formatPhoneForSubmission,
  formatPhoneForDisplay,
} from './zelle-preference-options';
import { colors, fonts } from '../../../style';
import ZelleIcon from '../../../images/zelle-logo.svg';
import EditIcon from '../../../images/edit-icon.svg';
import { CommonModal } from '../../base/common-modal';
import * as gateway from '../../../utils/web-apis-client/gateway';
import { log } from '../../../utils/log';
import FloatingValidationBanner from '../../forms/form/floating-validation-banner';
import { useLDClient } from '@launchdarkly/react-sdk';

const paymentMethods = {
  Zelle: {
    label: 'Zelle',
    id: 'zelle',
    context: `<a href="https://www.zellepay.com/" target="_blank">Zelle</a> \
       is a secure digital payment solution, where we send you funds approximately within a day, without needing your bank details.`,
  },
  EFT: {
    label: 'Direct Deposit',
    id: 'eft',
    context:
      'An electronic payment method, where we transfer funds approximately within 3 days, directly to your bank account.',
  },
  Check: {
    label: 'Mailed Paper Check',
    id: 'check',
    context:
      'A traditional payment method, where we send you a physical check in the mail, approximately within 7 days.',
  },
};

const AccountNumberLabel = styled.p`
  color: #031e47;
  font-family: ${fonts.openSans.regular.fontFamily};
  font-size: 16px;
  font-weight: ${fonts.openSans.regular.fontWeight};
  margin: 0;
`;

const CheckMethodLabel = styled(OpenSansSemiBoldLabelMod)`
  margin: 0;
  width: fit-content;
`;

const ControlContainer = styled.div`
  align-items: center;
  display: flex;
  padding-bottom: 10px;
  padding-left: 16px;
  padding-right: 16px;
  padding-top: 26px;

  > :first-child {
    margin-right: 8px;
  }

  > :last-child {
    margin-left: 8px;
  }
`;

const MutedLabel = styled.p`
  color: #657676;
  font-family: ${fonts.openSans.semibold.fontFamily};
  font-size: 13px;
  font-weight: ${fonts.openSans.semibold.fontWeight};
  margin: 0;
`;

const ContextLabel = styled.p`
  color: #767676;
  font-family: ${fonts.openSans.regular.fontFamily};
  font-size: 1rem;
  line-height: 1.375rem;
  font-weight: ${fonts.openSans.regular.fontWeight};
  margin: 0;
  margin-top: 5px;
  margin-left: 52px;
  margin-right: 20px;
  margin-bottom: 12px;
`;

const RadioGroupMod = styled.div`
  color: #525252;
  font-family: ${fonts.openSans.semibold.fontFamily};
  font-size: 15px;
  font-weight: ${fonts.openSans.semibold.fontWeight};
  line-height: 1.25;

  .k-radio {
    height: 24px;
    margin-left: 16px;
    width: 24px;
  }

  .k-radio::before {
    height: 16px;
    width: 16px;
  }

  .k-radio-label {
    margin-left: 11px;
  }
`;

const ZelleMethodLabel = styled(MutedLabel)`
  img {
    margin-right: 12px;
    vertical-align: text-top;
  }
`;

export const templateIds = {
  rootNode: 'payment-preferences',
  readOnlyTitle: 'payment-preferences-readonly-title',
  readOnlyEditButton: 'payment-preferences-readonly-edit-button',
  cancelButton: 'payment-preferences-cancel-button',
  submitButton: 'payment-preferences-submit-button',
  checkRadioButton: 'payment-preferences-check-radio-button',
  eftRadioButton: 'payment-preferences-eft-radio-button',
  zelleRadioButton: 'payment-preferences-zelle-radio-button',
};

export const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, templateIds);

const getInitialFormValues = (prefs: any) => {
  return {
    paymentMethod: get(prefs, 'paymentMethod', ''),
    [EftIds.accountNumberInput]: get(prefs, 'accountDetails.accountNo', ''),
    [EftIds.bankNameInput]: get(prefs, 'accountDetails.accountName', ''),
    [EftIds.routingNumberInput]: get(prefs, 'accountDetails.routingNumber', ''),
  };
};

const renderPaymentMethodRadioButton = (
  paymentMethod: any,
  processing: boolean,
  selectedPaymentMethod: any,
  handlePaymentMethodChange: Function,
  id: string,
  formRenderProps: any,
) => (
  <div className="pb-3">
    <RadioButton
      id={id}
      disabled={processing}
      name={'payment-preferences'}
      value={paymentMethod.id}
      checked={selectedPaymentMethod === paymentMethod.id}
      label={paymentMethod.label}
      onChange={(e) => handlePaymentMethodChange(e, formRenderProps)}
    />
    <ContextLabel
      dangerouslySetInnerHTML={getHtmlFromMarkdown(paymentMethod.context)}
    ></ContextLabel>
  </div>
);

const RenderCheckRadioAndOptions = (
  processing: boolean,
  selectedPaymentMethod: any,
  handlePaymentMethodChange: Function,
  automationIds: any,
  formRenderProps: any,
  address: any,
) => {
  return (
    <>
      <br />
      {renderPaymentMethodRadioButton(
        paymentMethods.Check,
        processing,
        selectedPaymentMethod,
        handlePaymentMethodChange,
        automationIds.checkRadioButton,
        formRenderProps,
      )}
      {selectedPaymentMethod === paymentMethods.Check.id && (
        <CheckPreferenceOptions address={address} />
      )}
    </>
  );
};

const RenderEFTRadioAndOptions = (
  processing: boolean,
  selectedPaymentMethod: any,
  handlePaymentMethodChange: Function,
  automationIds: any,
  formRenderProps: any,
) => {
  return (
    <>
      <br />
      {renderPaymentMethodRadioButton(
        paymentMethods.EFT,
        processing,
        selectedPaymentMethod,
        handlePaymentMethodChange,
        automationIds.eftRadioButton,
        formRenderProps,
      )}
      {selectedPaymentMethod === paymentMethods.EFT.id && (
        <EftPreferenceOptions
          processing={processing}
          bankNameLabel="Bank Name"
          routingNumberLabel="Routing Number"
          accountNumberLabel="Account Number"
        />
      )}
      <PrefSeparator />
    </>
  );
};

const RenderZelleRadioAndOptions = (
  processing: boolean,
  selectedPaymentMethod: any,
  handlePaymentMethodChange: Function,
  automationIds: any,
  formRenderProps: any,
  optionProps: any,
) => {
  return (
    <>
      {renderPaymentMethodRadioButton(
        paymentMethods.Zelle,
        processing,
        selectedPaymentMethod,
        handlePaymentMethodChange,
        automationIds.zelleRadioButton,
        formRenderProps,
      )}
      {selectedPaymentMethod === paymentMethods.Zelle.id && (
        <ZellePreferenceOptions {...optionProps} processing={processing} />
      )}
      <PrefSeparator />
    </>
  );
};

const renderReadOnlyContainer = (
  prefs: any,
  selectedPaymentMethod: any,
  zellePrefs: any,
  setEditMode: Function,
  id: string,
) => {
  return (
    <ReadOnlyContainer>
      <div>
        {selectedPaymentMethod === paymentMethods.Check.id && (
          <CheckMethodLabel>Mailed Paper Check</CheckMethodLabel>
        )}
        {selectedPaymentMethod === paymentMethods.Zelle.id && (
          <ZelleMethodLabel data-cb-mask="true">
            <span>
              <img src={ZelleIcon} alt="" />
            </span>
            Account:{' '}
            {zellePrefs.email
              ? zellePrefs.email
              : formatPhoneForDisplay(zellePrefs.phone)}
          </ZelleMethodLabel>
        )}
        {selectedPaymentMethod === paymentMethods.EFT.id && (
          <>
            <MutedLabel data-cb-mask="true">
              {get(prefs, 'eft.bankName', '')}
            </MutedLabel>
            <MutedLabel className="text-uppercase">Account Number</MutedLabel>
            <AccountNumberLabel data-cb-mask="true">
              {((val: string) =>
                val.length > 4
                  ? '•'.repeat(val.length - 4) + val.slice(-4)
                  : val)(get(prefs, 'eft.accountNum', ''))}
            </AccountNumberLabel>
          </>
        )}
      </div>
      <EditButtonMod id={id} onClick={() => setEditMode(true)}>
        <EditImage src={EditIcon} alt="" />
        Edit
      </EditButtonMod>
    </ReadOnlyContainer>
  );
};

const eftValidator = (values: any, eftValidation: any) => {
  const errObj: { [key: string]: any } = {};

  if (
    eftValidation?.err &&
    eftValidation.accountDetails.accountNo ===
      values[EftIds.accountNumberInput] &&
    eftValidation.accountDetails.routingNumber ===
      values[EftIds.routingNumberInput]
  ) {
    errObj[EftIds.accountNumberInput] = eftValidation.err;
    errObj[EftIds.routingNumberInput] = eftValidation.err;
  }

  return errObj;
};

const getUpdatePayload = (
  currentPrefs: any,
  selectedPaymentMethod: any,
  dataItem: any,
) => {
  const payload = { ...currentPrefs };
  payload.paymentMethod = selectedPaymentMethod;

  switch (selectedPaymentMethod) {
    case 'zelle':
      if (dataItem['paymentPhone']) {
        payload.zelle.phone = formatPhoneForSubmission(
          dataItem['paymentPhone'],
        );
      } else if (dataItem['paymentEmail']) {
        payload.zelle.email = dataItem['paymentEmail'];
      }
      break;

    case 'eft':
      payload.eft.bankName = dataItem[EftIds.bankNameInput];
      payload.eft.accountNum = dataItem[EftIds.accountNumberInput];
      payload.eft.routingNum = dataItem[EftIds.routingNumberInput];
      break;
  }

  return payload;
};

const getSetPaymentPreferences = async (
  setPrefs: Function,
  setSelectedPaymentMethod: Function,
  setLoading: Function,
  setToast: Function,
  setGetError: Function,
) => {
  try {
    const prefs = (await getPaymentPreferences()) as PaymentPreferencesModel;
    if (prefs) {
      setPrefs(prefs);
      setSelectedPaymentMethod(prefs.paymentMethod);
    }
  } catch (e: any) {
    log.error(e);
    setToast(toastMessages.getError, 'error');
    setGetError(true);
  } finally {
    setLoading(false);
  }
};

const handleFormSubmit = async (
  dataItem: any,
  prefs: any,
  selectedPaymentMethod: any,
  setProcessing: Function,
  setEftValidation: Function,
  setShowMFA: Function,
  enableStepUpAuthn: boolean,
): Promise<boolean | 'requiresMFA'> => {
  setProcessing(true);
  const payload = getUpdatePayload(prefs, selectedPaymentMethod, dataItem);

  try {
    if (enableStepUpAuthn) {
      const response = await gateway.postPaymentPreferences(payload);

      if (response.requiresStepUpAuth) {
        setShowMFA(true);
        return 'requiresMFA';
      }

      if (response.success) {
        return true;
      }
    } else {
      const response = await postPaymentPreferences(payload);

      if (response.ok) {
        return true;
      }
    }

    return false;
  } catch (err: any) {
    const msg = get(err, 'response.data.errorMessage', err.message);
    if (get(err, 'response.status', 0) === 400) {
      setEftValidation({ accountDetails: payload.accountDetails, err: msg });
    }
    return false;
  } finally {
    setProcessing(false);
  }
};

const SpinnerOnly = () => {
  return (
    <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-p-8">
      <StyledSpinner />
    </div>
  );
};

const Spinner = (
  closeToast: Function,
  displayToast: boolean,
  toastType: any,
  automationIds: any,
  message: string,
  getError: boolean,
) => {
  return (
    <Container id={automationIds.rootNode}>
      <ToastNotificationGroupStyle
        style={{
          top: 0,
          right: 0,
          alignItems: 'flex-end',
          position: 'fixed',
        }}
      >
        {displayToast && (
          <StyledNotification
            key="success"
            type={{ style: toastType, icon: true }}
            closable={true}
            onClose={() => closeToast}
          >
            {message}
          </StyledNotification>
        )}
      </ToastNotificationGroupStyle>
      <SectionTitleMod>Payments</SectionTitleMod>
      <SectionSeparator />
      <SectionSubTitleMod>
        You can change your payment method to one of our other options anytime.
      </SectionSubTitleMod>
      {getError ? (
        <ErrorText>
          There was an error fetching your preferences. Please{' '}
          <Link to={AppPaths.contactUs}>contact us</Link> to resolve this issue.
        </ErrorText>
      ) : (
        <SpinnerOnly />
      )}
    </Container>
  );
};

const getAddressFromPrefs = (check: any) => {
  const address: { [key: string]: string } = {};

  if (check?.address) {
    check.address.forEach((l: any, i: number) => {
      const lineNumber: number = i + 1;
      address['addressLine' + lineNumber] = l;
    });
    address['city'] = check.city;
    address['state'] = check.state;
    address['zip'] = check.zip;
  }
  return address;
};

const zelleLookupResult = (
  valid: boolean,
  isEmail: boolean,
  formRenderProps: any,
  setZelleValid: Function,
) => {
  setZelleValid(valid);
  if (valid) {
    formRenderProps.onChange(isEmail ? 'paymentEmail' : 'paymentPhone', {
      value: valid,
    });
  }
};

const handleCustomEvent = (
  event: any,
  setShowMFA: Function,
): undefined | 'retry' | 'error' => {
  log.debug('stepup-authentication-event', event.detail);

  //code, source, message
  if (event.detail.code === '200') {
    // retry transaction
    setShowMFA(false);
    return 'retry';
  } else if (event.detail.message === 'user clicked close') {
    setShowMFA(false);
  } else if (
    event.detail.message === 'user clicked close from verification failed'
  ) {
    setShowMFA(false);
  } else if (
    event.detail.message === 'user clicked close from edit access locked'
  ) {
    setShowMFA(false);
  } else if (event.detail.message === 'session token expired') {
    log.debug('401 event', event?.detail?.source);
    setShowMFA(false);
    return 'error';
  } else if (event.detail.code === '400' || event.detail.code === '500') {
    log.debug('API error event', event?.detail?.code, event?.detail?.source);
    setShowMFA(false);
    return 'error';
  }

  return undefined;
};

const PaymentPreferences = (props: any) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [editMode, setEditMode] = useState(false);
  const [eftValidation, setEftValidation] = useState({});
  const [processing, setProcessing] = useState(false);
  const [prefs, setPrefs] = useState<any>({});
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [zelleValid, setZelleValid] = useState(false);
  const [displayToast, setDisplayToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<ToastTypes['style']>('success');
  const [getError, setGetError] = useState<boolean>(false);
  const [showMFA, setShowMFA] = useState<boolean>(false);
  const [lastSubmission, setLastSubmission] = useState<any>(null);
  const { isRefreshing } = useContext(RefreshContext);
  const [validationErrorCount, setValidationErrorCount] = useState(0);
  const ldClient = useLDClient();

  const callSubmit = useCallback(
    async (dataItem?: any) => {
      if (dataItem) {
        setLastSubmission(dataItem);
      }

      // we should have at least 1 of these
      if (!dataItem && !lastSubmission) {
        return;
      }

      const success = await handleFormSubmit(
        dataItem || lastSubmission,
        prefs,
        selectedPaymentMethod,
        setProcessing,
        setEftValidation,
        setShowMFA,
        ldClient?.boolVariation('enable-stepup-auth-n', true),
      );

      // Ignore 'requiresMFA' response as it is handled by the modal
      if (success === true) {
        setToast(toastMessages.success, 'success');
        reset(selectedPaymentMethod);
      } else if (success === false) {
        setToast(toastMessages.postError, 'error');
      }
    },
    [lastSubmission, ldClient, prefs, selectedPaymentMethod],
  );

  const handleModalEvents = useCallback(
    (event: any) => {
      if (ldClient?.boolVariation('enable-stepup-auth-n', true)) {
        const result = handleCustomEvent(event, setShowMFA);

        if (result === 'retry') {
          callSubmit();
        } else if (result === 'error') {
          setToast(toastMessages.postError, 'error');
          reset(prefs.paymentMethod);
        }
      }
    },
    [callSubmit, ldClient, prefs.paymentMethod],
  );

  useEffect(() => {
    let ignore = false;
    getSetPaymentPreferences(
      (v: any) => {
        if (!ignore) setPrefs(v);
      },
      (v: any) => {
        if (!ignore) setSelectedPaymentMethod(v);
      },
      (v: any) => {
        if (!ignore) setLoading(v);
      },
      (m: string, t: any) => {
        if (!ignore) setToast(m, t);
      },
      (v: any) => {
        if (!ignore) setGetError(v);
      },
    );
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    document.addEventListener(
      'stepup-authentication-action',
      handleModalEvents,
    );

    return () => {
      document.removeEventListener(
        'stepup-authentication-action',
        handleModalEvents,
      );
    };
  }, [handleModalEvents]);

  const setToast = (message: string, type: any) => {
    setToastMessage(message);
    setToastType(type);
    setDisplayToast(true);
  };

  const closeToast = () => {
    setDisplayToast(false);
  };

  setTimeout(() => {
    if (displayToast) {
      closeToast();
    }
  }, 4000);

  const automationIds = domIdsUnique(props.keyId);
  const address = getAddressFromPrefs(prefs.check);
  const zelleEmail = props.userEmail;
  const initialFormValues = getInitialFormValues(prefs);

  const reset = (paymentMethod: any) => {
    setSelectedPaymentMethod(paymentMethod);
    setZelleValid(false);
    setEditMode(false);
    setEftValidation({});
    setLastSubmission(null);
  };

  const formValidator = (values: any) => {
    return eftValidator(values, eftValidation);
  };

  const handleCancelOnClick = () => {
    reset(prefs.paymentMethod);
  };

  const handlePaymentMethodChange = (e: any, formRenderProps: any) => {
    if (e.value === paymentMethods.Zelle.id) {
      setZelleValid(false);
    }
    setSelectedPaymentMethod(e.value);
    formRenderProps.onChange('paymentMethod', { value: e.value });
  };

  if (loading || getError) {
    return Spinner(
      closeToast,
      displayToast,
      toastType,
      automationIds,
      toastMessage,
      getError,
    );
  }

  return (
    <Container id={automationIds.rootNode}>
      <ToastNotificationGroupStyle
        style={{
          top: 0,
          right: 0,
          alignItems: 'flex-end',
          position: 'fixed',
        }}
      >
        {displayToast && (
          <StyledNotification
            key="success"
            type={{ style: toastType, icon: true }}
            closable={true}
            onClose={closeToast}
          >
            {toastMessage}
          </StyledNotification>
        )}
      </ToastNotificationGroupStyle>
      <SectionTitleMod>Payments</SectionTitleMod>
      <SectionSeparator />
      <SectionSubTitleMod>
        You can change your payment method to one of our other options anytime.
      </SectionSubTitleMod>
      <br />
      {!editMode &&
        renderReadOnlyContainer(
          prefs,
          selectedPaymentMethod,
          prefs.zelle,
          setEditMode,
          automationIds.readOnlyEditButton,
        )}
      {editMode && (
        <Form
          initialValues={initialFormValues}
          onSubmit={callSubmit}
          validator={formValidator}
          render={(formRenderProps) => {
            const paymentMethodLabel: Record<string, string> = {
              zelle: 'zelle',
              eft: 'direct deposit',
              check: 'mailed paper check',
            };
            const gtmAttribute4 =
              paymentMethodLabel[selectedPaymentMethod] ?? 'undefined';

            return (
              <FormElement>
                <fieldset className="k-form-fieldset">
                  <FloatingValidationBanner
                    errorCount={validationErrorCount}
                    onDismiss={() => setValidationErrorCount(0)}
                    setErrorCount={setValidationErrorCount}
                  />
                  <RadioGroupMod>
                    {RenderZelleRadioAndOptions(
                      processing,
                      selectedPaymentMethod,
                      handlePaymentMethodChange,
                      automationIds,
                      formRenderProps,
                      {
                        resultCallback: (valid: any, isEmail: boolean) =>
                          zelleLookupResult(
                            valid,
                            isEmail,
                            formRenderProps,
                            setZelleValid,
                          ),
                        currentEmailPref: zelleEmail,
                        currentPaymentMethod: prefs.paymentMethod,
                      },
                    )}
                    {RenderEFTRadioAndOptions(
                      processing,
                      selectedPaymentMethod,
                      handlePaymentMethodChange,
                      automationIds,
                      formRenderProps,
                    )}
                    {RenderCheckRadioAndOptions(
                      processing,
                      selectedPaymentMethod,
                      handlePaymentMethodChange,
                      automationIds,
                      formRenderProps,
                      address,
                    )}
                  </RadioGroupMod>
                  <ControlContainer>
                    <CancelButton
                      id={automationIds.cancelButton}
                      className="k-button"
                      onClick={handleCancelOnClick}
                      buttonStyle={ButtonTypes.secondary}
                      disabled={processing}
                    >
                      Cancel
                    </CancelButton>
                    <SubmitButton
                      id={automationIds.submitButton}
                      className="k-button"
                      onClick={(e: any) => {
                        formRenderProps.onSubmit(e);
                        setTimeout(() => {
                          const errorElements =
                            document.querySelectorAll('[id$="_error"]');
                          if (errorElements.length > 0) {
                            setValidationErrorCount(errorElements.length);
                          }
                        }, 0);
                      }}
                      disabled={
                        (!zelleValid &&
                          selectedPaymentMethod === paymentMethods.Zelle.id) ||
                        processing ||
                        isRefreshing
                      }
                      data-gtm-dl={JSON.stringify({
                        attribute1: 'preferences',
                        attribute2: 'undefined',
                        attribute3: 'submit_payment_preference',
                        attribute4: gtmAttribute4,
                      })}
                    >
                      Submit
                    </SubmitButton>
                    <ClipLoader
                      size={25}
                      loading={processing}
                      color={colors.primaryBlue}
                    />
                  </ControlContainer>
                </fieldset>
              </FormElement>
            );
          }}
        />
      )}
      {showMFA && (
        <CommonModal
          type="stepup-auth"
          modalCallback={() => setShowMFA(false)}
        />
      )}
    </Container>
  );
};

export default PaymentPreferences;
export { eftValidator, getUpdatePayload };
